<script setup>
import Card from "primevue/card";
import Tree from "primevue/tree";
import { computed } from "vue";
import ApartmentIcon from "@/components/shared/icons/ApartmentIcon.vue";
import PersonShield from "@/components/shared/icons/PersonShield.vue";
import PlusShoppingCartIcon from "@/components/shared/icons/PlusShoppingCartIcon.vue";
import SchoolIcon from "@/components/shared/icons/SchoolIcon.vue";
import ToolBoxIcon from "@/components/shared/icons/ToolBoxIcon.vue";
import { LocalActivityTicketIcon } from "@/components/svg-icons/index.js";
import { showNewExcursionConfiguratorDesign } from "@/composables/authorization.js";

const { experienceDetail } = defineProps({
  experienceDetail: {
    type: Object,
    required: true,
  },
});

const treeNodeDefinitions = [
  {
    key: "internships",
    label: "Internship Placement Selections",
    icon: ToolBoxIcon,
    children: [],
    leaf: false,
  },
  {
    key: "courses",
    label: "Courses",
    icon: SchoolIcon,
    children: [],
    leaf: false,
  },
  {
    key: "housing",
    label: "Housing",
    icon: ApartmentIcon,
    children: [],
    leaf: false,
  },
  {
    key: "excursions",
    label: "Excursions",
    icon: LocalActivityTicketIcon,
    children: [],
    leaf: false,
  },
  {
    key: "insurance",
    label: "Insurance",
    icon: PersonShield,
    children: [],
    leaf: false,
  },
  {
    key: "addons",
    label: "Add-Ons",
    icon: PlusShoppingCartIcon,
    children: [],
    leaf: false,
  },
];

const activeTreeNodes = computed(() => {
  const createChildren = (names) => {
    const children = [];
    for (const name of names) {
      children.push({
        key: name,
        label: name,
        leaf: true,
      });
    }
    return children;
  };

  for (const node of treeNodeDefinitions) {
    const courses = experienceDetail.misc_product_names.filter((name) => {
      return name.toLowerCase().includes("classes");
    });
    const insurances = experienceDetail.misc_product_names.filter((name) => {
      return name.toLowerCase().includes("insurance");
    });
    const addons = experienceDetail.misc_product_names.filter((name) => {
      return !courses.includes(name) && !insurances.includes(name);
    });
    if (node.key === "internships") {
      node.children = createChildren(experienceDetail.internship_names);
    } else if (node.key === "courses") {
      node.children = createChildren(courses);
    } else if (node.key === "housing") {
      node.children = createChildren(experienceDetail.housing_type_names);
    } else if (node.key === "excursions") {
      node.label = showNewExcursionConfiguratorDesign.value ? "Curated Experiences" : "Excursions";
      node.children = createChildren(experienceDetail.event_names);
    } else if (node.key === "insurance") {
      node.children = createChildren(insurances);
    } else if (node.key === "addons") {
      node.children = createChildren(addons);
    }
  }
  return treeNodeDefinitions.filter((node) => node.children.length > 0);
});

const treeCardPt = {
  root: {
    class: "p-0 bg-blue-350 border-blue-250 rounded-[12px]",
  },
  header: {
    class: "text-indigo-base text-base text-left font-bold p-4 font-montserrat text-sm",
  },
  body: {
    class: "px-2",
  },
  content: {
    class: "",
  },
};

const treePt = {
  root: {
    class: "",
  },
  content: {
    class: "flex items-center text-left font-montserrat text-sm",
  },
  subgroup: {
    class: "m-0 list-none p-0",
  },
};

const MyExperiencesSelectionsThemeOverride = {
  nodeicon: {
    class: ["text-surface-600 dark:text-white/70"],
  },
};

const expandedTreeNodes = computed(() => Object.fromEntries(activeTreeNodes.value.map((node) => [node.key, true])));
</script>
<template>
  <Card :pt="treeCardPt">
    <template #header>
      <span class="">My Experience Selections</span>
    </template>
    <template #content>
      <Tree v-model:expanded-keys="expandedTreeNodes" :value="activeTreeNodes" :pt="treePt">
        <template #default="slotProps">
          <div class="flex items-center text-indigo-base text-sm" :pt="MyExperiencesSelectionsThemeOverride">
            <component :is="slotProps.node.icon" v-if="slotProps.node.icon" class="!w-5 !h-5" stroke="None" />
            <span :class="slotProps.node.leaf ? '' : 'font-bold ml-1 text-wrap'">{{ slotProps.node.label }}</span>
          </div>
        </template>
      </Tree>
    </template>
  </Card>
</template>
